const DEFAULT_BLOB_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const DEFAULT_BLOB_CACHE_MAX_ENTRIES = 200;

type BlobCacheEntry = {
  url: string;
  expiresAt: number;
};

export class BlobUrlCache {
  private entries = new Map<string, BlobCacheEntry>();
  private urlToFileId = new Map<string, string>();
  private inflight = new Map<string, Promise<string>>();
  private readonly ttlMs: number;
  private readonly maxEntries: number;

  constructor({
    ttlMs,
    maxEntries,
  }: { ttlMs?: number; maxEntries?: number } = {}) {
    this.ttlMs = ttlMs ?? DEFAULT_BLOB_CACHE_TTL_MS;
    this.maxEntries = maxEntries ?? DEFAULT_BLOB_CACHE_MAX_ENTRIES;
  }

  get(fileId: string): string | null {
    this.pruneExpired();
    const entry = this.entries.get(fileId);
    if (!entry) return null;
    if (entry.expiresAt <= Date.now()) {
      this.deleteEntry(fileId, entry);
      return null;
    }
    this.touch(fileId, entry);
    return entry.url;
  }

  hasUrl(url: string): boolean {
    return this.urlToFileId.has(url);
  }

  touchByUrl(url: string) {
    const fileId = this.urlToFileId.get(url);
    if (!fileId) return;
    const entry = this.entries.get(fileId);
    if (!entry || entry.url !== url) return;
    this.touch(fileId, entry);
  }

  async getOrCreate(
    fileId: string,
    loader: () => Promise<string>
  ): Promise<string> {
    const cached = this.get(fileId);
    if (cached) return cached;

    const inflight = this.inflight.get(fileId);
    if (inflight) return inflight;

    const promise = loader()
      .then((url) => {
        this.inflight.delete(fileId);
        this.set(fileId, url);
        return url;
      })
      .catch((error) => {
        this.inflight.delete(fileId);
        throw error;
      });

    this.inflight.set(fileId, promise);
    return promise;
  }

  private set(fileId: string, url: string) {
    const existing = this.entries.get(fileId);
    if (existing && existing.url !== url) {
      this.urlToFileId.delete(existing.url);
      URL.revokeObjectURL(existing.url);
    }

    const entry = {
      url,
      expiresAt: Date.now() + this.ttlMs,
    };
    this.entries.delete(fileId);
    this.entries.set(fileId, entry);
    this.urlToFileId.set(url, fileId);

    this.pruneExpired();
    this.evictOverflow();
  }

  private touch(fileId: string, entry: BlobCacheEntry) {
    this.entries.delete(fileId);
    this.entries.set(fileId, {
      url: entry.url,
      expiresAt: Date.now() + this.ttlMs,
    });
  }

  private pruneExpired() {
    const now = Date.now();
    for (const [fileId, entry] of this.entries) {
      if (entry.expiresAt <= now) {
        this.deleteEntry(fileId, entry);
      }
    }
  }

  private evictOverflow() {
    while (this.entries.size > this.maxEntries) {
      const oldest = this.entries.keys().next().value as string | undefined;
      if (!oldest) return;
      const entry = this.entries.get(oldest);
      if (!entry) {
        this.entries.delete(oldest);
        continue;
      }
      this.deleteEntry(oldest, entry);
    }
  }

  private deleteEntry(fileId: string, entry: BlobCacheEntry) {
    this.entries.delete(fileId);
    this.urlToFileId.delete(entry.url);
    URL.revokeObjectURL(entry.url);
  }
}

export const blobUrlCache = new BlobUrlCache();
